/**
 * Codal Reference Service v1.0
 * 
 * Quick reference service for Philippine legal codes
 * Provides instant access to key provisions with AI explanation integration
 */

import { ALL_ADDITIONAL_PROVISIONS } from './codal/additionalProvisions';

// ==================== Types ======================================

export type CodalCode =
    | 'CIVIL_CODE'
    | 'REVISED_PENAL_CODE'
    | 'CORPORATION_CODE'
    | 'LABOR_CODE'
    | 'FAMILY_CODE'
    | 'TAX_CODE'
    | 'RULES_OF_COURT'
    | 'CONSTITUTION'
    | 'INSURANCE_CODE';

export interface CodalProvision {
    code: CodalCode;
    article: string; // e.g., "Art. 1156" or "Sec. 12"
    title: string;
    text: string;
    chapter?: string;
    book?: string;
    notes?: string;
    keywords: string[];
}

// ==================== Civil Code Provisions ====================

const CIVIL_CODE_PROVISIONS: CodalProvision[] = [
    // Book IV - Obligations and Contracts
    {
        code: 'CIVIL_CODE',
        article: 'Art. 1156',
        title: 'Definition of Obligation',
        book: 'Book IV - Obligations and Contracts',
        chapter: 'Title I - Obligations',
        text: 'An obligation is a juridical necessity to give, to do or not to do.',
        keywords: ['obligation', 'definition', 'juridical necessity', 'prestation'],
    },
    {
        code: 'CIVIL_CODE',
        article: 'Art. 1157',
        title: 'Sources of Obligations',
        book: 'Book IV - Obligations and Contracts',
        chapter: 'Title I - Obligations',
        text: 'Obligations arise from: (1) Law; (2) Contracts; (3) Quasi-contracts; (4) Acts or omissions punished by law; and (5) Quasi-delicts.',
        keywords: ['sources', 'law', 'contracts', 'quasi-contracts', 'delicts', 'quasi-delicts'],
    },
    {
        code: 'CIVIL_CODE',
        article: 'Art. 1159',
        title: 'Obligatory Force of Contracts',
        book: 'Book IV - Obligations and Contracts',
        chapter: 'Title I - Obligations',
        text: 'Obligations arising from contracts have the force of law between the contracting parties and should be complied with in good faith.',
        keywords: ['contracts', 'force of law', 'good faith', 'pacta sunt servanda'],
    },
    {
        code: 'CIVIL_CODE',
        article: 'Art. 1163',
        title: 'Obligation to Give - Determinate Thing',
        book: 'Book IV - Obligations and Contracts',
        chapter: 'Title I - Obligations',
        text: 'Every person obliged to give something is also obliged to take care of it with the proper diligence of a good father of a family, unless the law or the stipulation of the parties requires another standard of care.',
        keywords: ['obligation to give', 'determinate thing', 'diligence', 'good father of a family', 'bonus pater familias'],
    },
    {
        code: 'CIVIL_CODE',
        article: 'Art. 1169',
        title: 'Default (Mora)',
        book: 'Book IV - Obligations and Contracts',
        chapter: 'Title I - Obligations',
        text: 'Those obliged to deliver or to do something incur in delay from the time the obligee judicially or extrajudicially demands from them the fulfillment of their obligation.',
        keywords: ['default', 'mora', 'delay', 'demand', 'judicial', 'extrajudicial'],
    },
    {
        code: 'CIVIL_CODE',
        article: 'Art. 1174',
        title: 'Fortuitous Event',
        book: 'Book IV - Obligations and Contracts',
        chapter: 'Title I - Obligations',
        text: 'Except in cases expressly specified by the law, or when it is otherwise declared by stipulation, or when the nature of the obligation requires the assumption of risk, no person shall be responsible for those events which could not be foreseen, or which, though foreseen, were inevitable.',
        keywords: ['fortuitous event', 'force majeure', 'act of God', 'inevitable', 'unforeseen'],
    },
    {
        code: 'CIVIL_CODE',
        article: 'Art. 1191',
        title: 'Rescission in Reciprocal Obligations',
        book: 'Book IV - Obligations and Contracts',
        chapter: 'Title I - Obligations',
        text: 'The power to rescind obligations is implied in reciprocal ones, in case one of the obligors should not comply with what is incumbent upon him. The injured party may choose between the fulfillment and the rescission of the obligation, with the payment of damages in either case.',
        keywords: ['rescission', 'reciprocal obligations', 'breach', 'fulfillment', 'damages'],
    },
    // Contracts
    {
        code: 'CIVIL_CODE',
        article: 'Art. 1305',
        title: 'Definition of Contract',
        book: 'Book IV - Obligations and Contracts',
        chapter: 'Title II - Contracts',
        text: 'A contract is a meeting of minds between two persons whereby one binds himself, with respect to the other, to give something or to render some service.',
        keywords: ['contract', 'definition', 'meeting of minds', 'consent'],
    },
    {
        code: 'CIVIL_CODE',
        article: 'Art. 1306',
        title: 'Freedom of Contract',
        book: 'Book IV - Obligations and Contracts',
        chapter: 'Title II - Contracts',
        text: 'The contracting parties may establish such stipulations, clauses, terms and conditions as they may deem convenient, provided they are not contrary to law, morals, good customs, public order, or public policy.',
        keywords: ['freedom of contract', 'autonomy', 'stipulations', 'public policy', 'morals'],
    },
    {
        code: 'CIVIL_CODE',
        article: 'Art. 1318',
        title: 'Essential Requisites of Contracts',
        book: 'Book IV - Obligations and Contracts',
        chapter: 'Title II - Contracts',
        text: 'There is no contract unless the following requisites concur: (1) Consent of the contracting parties; (2) Object certain which is the subject matter of the contract; (3) Cause of the obligation which is established.',
        keywords: ['requisites', 'consent', 'object', 'cause', 'essential elements'],
    },
    // Sales
    {
        code: 'CIVIL_CODE',
        article: 'Art. 1458',
        title: 'Definition of Sale',
        book: 'Book IV - Obligations and Contracts',
        chapter: 'Title VI - Sales',
        text: 'By the contract of sale one of the contracting parties obligates himself to transfer the ownership and to deliver a determinate thing, and the other to pay therefor a price certain in money or its equivalent.',
        keywords: ['sale', 'definition', 'transfer of ownership', 'price', 'delivery'],
    },
    {
        code: 'CIVIL_CODE',
        article: 'Art. 1477',
        title: 'Traditio - Delivery in Sale',
        book: 'Book IV - Obligations and Contracts',
        chapter: 'Title VI - Sales',
        text: 'The ownership of the thing sold shall be transferred to the vendee upon the actual or constructive delivery thereof.',
        keywords: ['traditio', 'delivery', 'ownership transfer', 'constructive delivery'],
    },
    // Agency
    {
        code: 'CIVIL_CODE',
        article: 'Art. 1868',
        title: 'Definition of Agency',
        book: 'Book IV - Obligations and Contracts',
        chapter: 'Title X - Agency',
        text: 'By the contract of agency a person binds himself to render some service or to do something in representation or on behalf of another, with the consent or authority of the latter.',
        keywords: ['agency', 'definition', 'representation', 'principal', 'agent'],
    },
    // Partnership
    {
        code: 'CIVIL_CODE',
        article: 'Art. 1767',
        title: 'Definition of Partnership',
        book: 'Book IV - Obligations and Contracts',
        chapter: 'Title IX - Partnership',
        text: 'By the contract of partnership two or more persons bind themselves to contribute money, property, or industry to a common fund, with the intention of dividing the profits among themselves.',
        keywords: ['partnership', 'definition', 'contribution', 'common fund', 'profits'],
    },
    // Torts
    {
        code: 'CIVIL_CODE',
        article: 'Art. 2176',
        title: 'Quasi-Delict (Tort)',
        book: 'Book IV - Obligations and Contracts',
        chapter: 'Title XVII - Extra-Contractual Obligations',
        text: 'Whoever by act or omission causes damage to another, there being fault or negligence, is obliged to pay for the damage done. Such fault or negligence, if there is no pre-existing contractual relation between the parties, is called a quasi-delict.',
        keywords: ['quasi-delict', 'tort', 'negligence', 'fault', 'damages', 'culpa aquiliana'],
    },
];

// ==================== Revised Penal Code Provisions ====================

const REVISED_PENAL_CODE_PROVISIONS: CodalProvision[] = [
    // Book I - General Principles
    {
        code: 'REVISED_PENAL_CODE',
        article: 'Art. 3',
        title: 'Definition of Felonies',
        book: 'Book I - General Principles',
        text: 'Acts and omissions punishable by law are felonies (delitos). Felonies are committed not only by means of deceit (dolo) but also by means of fault (culpa).',
        keywords: ['felony', 'delito', 'dolo', 'culpa', 'deceit', 'fault'],
    },
    {
        code: 'REVISED_PENAL_CODE',
        article: 'Art. 4',
        title: 'Criminal Liability',
        book: 'Book I - General Principles',
        text: 'Criminal liability shall be incurred: (1) By any person committing a felony (delito) although the wrongful act done be different from that which he intended; (2) By any person performing an act which would be an offense against persons or property, were it not for the inherent impossibility of its accomplishment or on account of the employment of inadequate or ineffectual means.',
        keywords: ['criminal liability', 'praeter intentionem', 'impossible crime', 'aberratio ictus'],
    },
    {
        code: 'REVISED_PENAL_CODE',
        article: 'Art. 11',
        title: 'Justifying Circumstances',
        book: 'Book I - General Principles',
        text: 'The following do not incur any criminal liability: 1. Anyone who acts in defense of his person or rights... 2. Anyone who acts in defense of the person or rights of his spouse, ascendants, descendants, or legitimate, natural or adopted brothers or sisters...',
        keywords: ['justifying circumstances', 'self-defense', 'defense of relatives', 'defense of stranger', 'state of necessity', 'fulfillment of duty'],
    },
    {
        code: 'REVISED_PENAL_CODE',
        article: 'Art. 12',
        title: 'Exempting Circumstances',
        book: 'Book I - General Principles',
        text: 'The following are exempt from criminal liability: 1. An imbecile or an insane person... 2. A person under nine years of age... 3. A person over nine years of age and under fifteen... 4. Any person who, while performing a lawful act with due care, causes an injury by mere accident...',
        keywords: ['exempting circumstances', 'insanity', 'minority', 'accident', 'compulsion', 'irresistible force'],
    },
    {
        code: 'REVISED_PENAL_CODE',
        article: 'Art. 13',
        title: 'Mitigating Circumstances',
        book: 'Book I - General Principles',
        text: 'Mitigating circumstances: 1. Those mentioned in the preceding chapter, when all the requisites necessary to justify or to exempt from criminal liability in the respective cases are not attendant... 2. That the offender is under eighteen year of age or over seventy years...',
        keywords: ['mitigating circumstances', 'incomplete justification', 'passion', 'provocation', 'voluntary surrender', 'plea of guilty'],
    },
    {
        code: 'REVISED_PENAL_CODE',
        article: 'Art. 14',
        title: 'Aggravating Circumstances',
        book: 'Book I - General Principles',
        text: 'Aggravating circumstances: 1. That advantage be taken by the offender of his public position... 2. That the crime be committed in contempt or with insult to the public authorities... 3. That the act be committed with insult or in disregard of the respect due the offended party...',
        keywords: ['aggravating circumstances', 'treachery', 'abuse of confidence', 'recidivism', 'evident premeditation', 'cruelty'],
    },
];

// ==================== Corporation Code (RCC) Provisions ====================

const CORPORATION_CODE_PROVISIONS: CodalProvision[] = [
    {
        code: 'CORPORATION_CODE',
        article: 'Sec. 2',
        title: 'Definition of Corporation',
        text: 'A corporation is an artificial being created by operation of law, having the right of succession and the powers, attributes, and properties expressly authorized by law or incidental to its existence.',
        keywords: ['corporation', 'definition', 'artificial being', 'juridical person', 'legal entity'],
    },
    {
        code: 'CORPORATION_CODE',
        article: 'Sec. 10',
        title: 'Number and Qualifications of Incorporators',
        text: 'Any person, partnership, association or corporation, singly or jointly with others but not more than fifteen (15) in number, may organize a corporation for any lawful purpose or purposes.',
        notes: 'RA 11232 allows a One Person Corporation (OPC)',
        keywords: ['incorporators', 'requirements', 'one person corporation', 'OPC'],
    },
    {
        code: 'CORPORATION_CODE',
        article: 'Sec. 22',
        title: 'Corporate Powers',
        text: 'Every corporation incorporated under this Code has the power and capacity: (1) To sue and be sued in its corporate name; (2) Of succession by its corporate name for the period of time stated in its articles of incorporation; (3) To adopt and use a corporate seal...',
        keywords: ['corporate powers', 'sue and be sued', 'succession', 'corporate seal', 'by-laws'],
    },
    {
        code: 'CORPORATION_CODE',
        article: 'Sec. 30',
        title: 'Board of Directors',
        text: 'The corporate powers of all corporations formed under this Code shall be exercised, all business conducted and all property of such corporations controlled and held by the board of directors or trustees...',
        keywords: ['board of directors', 'corporate powers', 'trustees', 'management'],
    },
    {
        code: 'CORPORATION_CODE',
        article: 'Sec. 130',
        title: 'One Person Corporation',
        text: 'A One Person Corporation is a corporation with a single stockholder: Provided, That only a natural person, trust, or an estate may form a One Person Corporation.',
        keywords: ['one person corporation', 'OPC', 'single stockholder', 'sole proprietor alternative'],
    },
];

// ==================== All Provisions Combined ====================

export const ALL_CODAL_PROVISIONS: CodalProvision[] = [
    ...CIVIL_CODE_PROVISIONS,
    ...REVISED_PENAL_CODE_PROVISIONS,
    ...CORPORATION_CODE_PROVISIONS,
    ...ALL_ADDITIONAL_PROVISIONS,
];

// ==================== Code Metadata ====================

export const CODAL_CODES: Record<CodalCode, { name: string; shortName: string; description: string }> = {
    CIVIL_CODE: {
        name: 'Civil Code of the Philippines',
        shortName: 'Civil Code',
        description: 'Republic Act No. 386 - Governs civil relations, obligations, contracts, property, and family relations.',
    },
    REVISED_PENAL_CODE: {
        name: 'Revised Penal Code',
        shortName: 'RPC',
        description: 'Act No. 3815 - Defines crimes, penalties, and criminal liability in the Philippines.',
    },
    CORPORATION_CODE: {
        name: 'Revised Corporation Code',
        shortName: 'RCC',
        description: 'Republic Act No. 11232 - Governs the creation, organization, and regulation of private corporations.',
    },
    LABOR_CODE: {
        name: 'Labor Code of the Philippines',
        shortName: 'Labor Code',
        description: 'Presidential Decree No. 442 - Governs employment, labor relations, and social legislation.',
    },
    FAMILY_CODE: {
        name: 'Family Code of the Philippines',
        shortName: 'Family Code',
        description: 'Executive Order No. 209 - Governs marriage, family relations, property relations, and support.',
    },
    TAX_CODE: {
        name: 'National Internal Revenue Code',
        shortName: 'NIRC/Tax Code',
        description: 'Republic Act No. 8424 as amended - Governs national taxation, income tax, VAT, and other internal revenue taxes.',
    },
    RULES_OF_COURT: {
        name: 'Rules of Court',
        shortName: 'Rules of Court',
        description: 'Procedural rules governing pleadings, practice, procedure, evidence, and appeals in Philippine courts.',
    },
    CONSTITUTION: {
        name: '1987 Constitution of the Philippines',
        shortName: 'Constitution',
        description: 'The fundamental law of the land defining the structure of government and ensuring basic rights.',
    },
    INSURANCE_CODE: {
        name: 'Insurance Code of the Philippines',
        shortName: 'Insurance Code',
        description: 'Presidential Decree No. 612 as amended - Governs insurance contracts and the insurance industry.',
    },
};

// ==================== Service Functions ====================

/**
 * Search for codal provisions by keyword
 */
export function searchCodalProvisions(query: string): CodalProvision[] {
    const lowerQuery = query.toLowerCase();

    return ALL_CODAL_PROVISIONS.filter(provision => {
        return (
            provision.article.toLowerCase().includes(lowerQuery) ||
            provision.title.toLowerCase().includes(lowerQuery) ||
            provision.text.toLowerCase().includes(lowerQuery) ||
            provision.keywords.some(k => k.toLowerCase().includes(lowerQuery))
        );
    }).slice(0, 10); // Limit to 10 results
}

/**
 * Get a specific provision by article number
 */
export function getProvisionByArticle(article: string): CodalProvision | undefined {
    const normalizedArticle = article.toLowerCase().replace(/\s+/g, ' ').trim();

    return ALL_CODAL_PROVISIONS.find(provision => {
        const provArticle = provision.article.toLowerCase().replace(/\s+/g, ' ').trim();
        return provArticle === normalizedArticle || provArticle.includes(normalizedArticle);
    });
}

/**
 * Get all provisions for a specific code
 */
export function getProvisionsByCode(code: CodalCode): CodalProvision[] {
    return ALL_CODAL_PROVISIONS.filter(provision => provision.code === code);
}

/**
 * Generate AI prompt context for a provision
 */
export function generateProvisionContext(provision: CodalProvision): string {
    return `
📜 **CODAL REFERENCE**

**Code:** ${CODAL_CODES[provision.code].name}
**Provision:** ${provision.article} - ${provision.title}
${provision.book ? `**Book:** ${provision.book}` : ''}
${provision.chapter ? `**Chapter:** ${provision.chapter}` : ''}

**Text:**
"${provision.text}"

${provision.notes ? `**Notes:** ${provision.notes}` : ''}

Please explain this provision in the context of Philippine law, including:
1. What it means in practical terms
2. How it's commonly applied
3. Related provisions (if any)
4. Important Supreme Court cases interpreting this provision
`;
}

/**
 * Get related provisions based on keywords
 */
export function getRelatedProvisions(provision: CodalProvision, limit: number = 5): CodalProvision[] {
    return ALL_CODAL_PROVISIONS
        .filter(p => p.article !== provision.article)
        .filter(p => {
            return provision.keywords.some(k => p.keywords.includes(k));
        })
        .slice(0, limit);
}

export default {
    searchCodalProvisions,
    getProvisionByArticle,
    getProvisionsByCode,
    generateProvisionContext,
    getRelatedProvisions,
    ALL_CODAL_PROVISIONS,
    CODAL_CODES,
};
